import { Loader2, CheckCircle2, AlertCircle, RefreshCw, Sparkles } from 'lucide-react';
import type { GenerationStatus } from '@/types/content';
import { Badge, type BadgeProps } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface GenerationStatusCellProps {
  status?: GenerationStatus;
  error?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}

/**
 * AI content generation status with a retry button for FAILED.
 *
 * Requirements: 10.7, 10.8
 */
export function GenerationStatusCell({ status, error, onRetry, isRetrying = false }: GenerationStatusCellProps) {
  if (!status || status === 'NONE') {
    return <span className="text-xs text-muted-foreground">N/A</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <GenerationStatusBadge status={status} />
        {status === 'FAILED' && onRetry && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRetry();
            }}
            disabled={isRetrying}
            className="h-6 px-1.5 text-xs"
            title="Retry content generation"
          >
            {isRetrying ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            Retry
          </Button>
        )}
      </div>
      {status === 'FAILED' && error && (
        <p className="max-w-xs truncate text-xs text-destructive" title={error}>
          {error}
        </p>
      )}
    </div>
  );
}

const CONFIG: Record<GenerationStatus, { icon: typeof Sparkles; variant: NonNullable<BadgeProps['variant']>; label: string; spin?: boolean }> = {
  GENERATING: { icon: Loader2, variant: 'info', label: 'Generating', spin: true },
  GENERATED: { icon: CheckCircle2, variant: 'success', label: 'Generated' },
  FAILED: { icon: AlertCircle, variant: 'destructive', label: 'Failed' },
  NONE: { icon: Sparkles, variant: 'muted', label: 'Manual' },
};

export function GenerationStatusBadge({ status }: { status: GenerationStatus }) {
  const { icon: Icon, variant, label, spin } = CONFIG[status] || CONFIG.NONE;
  return (
    <Badge variant={variant}>
      <Icon className={spin ? 'animate-spin' : undefined} aria-hidden />
      {label}
    </Badge>
  );
}
